import Vue from 'vue'
import { SeriesResponse, VideoResponse } from '@/types/cms';
import Vuex, { StoreOptions } from 'vuex'
import Api from '@/api'
import { Language } from '@/enums/language'
import { State } from '@/types/State';


import LiveConfig from '@/types/LiveConfig';
import { TVSystem } from '@/enums/tv-system';
import { User } from '@/types/User';
import { TG4Region } from '@/types/cms';

Vue.use(Vuex)

const INITIAL_LAUNCH_KEY = 'initialLaunch-v2';

const store: StoreOptions<State> = {
  state: {
    get currentLanguage(): Language {
      if (localStorage && localStorage.getItem('currentLanguage')) {
        const currentLanguageStored = localStorage.getItem('currentLanguage');
        if (currentLanguageStored) {
          return currentLanguageStored as Language;
        }
        return Language.Irish;
      }
      return Language.Irish;
    },
    set currentLanguage(value) {
      localStorage.setItem('currentLanguage', value);
    },
    debugModeEnabled: false,
    useOnScreenKeyboardForSearch: false,
    apiLocation: {
      region: TG4Region.WorldWide,
      countryCode: null,
      city: null
    },
    liveConfig: {
      smartTVLoggedInAdConfig: "1a0d4f40-c9d0-402d-9c28-0efd8cc97756",
      smartTVLoggedOutAdConfig: "1a0d4f40-c9d0-402d-9c28-0efd8cc97756",
    },
    get token(): string {
      if (localStorage && localStorage.getItem('token')) {
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
          return storedToken
        }
        return ""
      }
      return ""
    },
    set token(value) {
      localStorage.setItem('token', value);
    },
    get region(): TG4Region {
      if (localStorage && localStorage.getItem('region')) {
        const currentRegionStored = localStorage.getItem('region');
        if (currentRegionStored) {
          return currentRegionStored as TG4Region;
        }
        return TG4Region.WorldWide;
      }
      return TG4Region.WorldWide;
    },
    set region(value) {
      localStorage.setItem('region', value);
    },
    // Presence of the key is what marks onboarding as done, not its value.
    get initialLaunch(): boolean {
      if (localStorage && localStorage.getItem(INITIAL_LAUNCH_KEY)) {
        return false;
      }
      return true;
    },
    set initialLaunch(value) {
      localStorage.setItem(INITIAL_LAUNCH_KEY, `${value}`);
    },
    tvInfo: {
      platformVersion: '',
      platformMajorVersion: undefined
    },
    tvSystem: TVSystem.OTHER,
    user: {
      id: -1,
      firstname: undefined,
      lastname: undefined,
      email: undefined
    },
    bookmarks: [],
    cacheKey: (Math.random() + 1).toString(36).substring(7),
    episodeMap: {},
    seriesMap: {}
  },
  mutations: {
    INITIAL_SETUP_COMPLETE(state) {
      state.initialLaunch = false;
    },
    CHANGE_LANGUAGE(state, language: Language) {
      state.currentLanguage = language;
    },
    CHANGE_DEBUG_MODE(state, debugeMode: boolean) {
      state.debugModeEnabled = debugeMode;
    },
    SET_USES_ONSCREEN_KEYBOARD(state, useOnScreenKeyboardForSearch: boolean){
      state.useOnScreenKeyboardForSearch = useOnScreenKeyboardForSearch
    },
    SET_TOKEN(state, token: string) {
      state.token = token
    },
    LOGOUT(state) {
      state.token = ""
      state.user = {
        id: -1,
        firstname: undefined,
        lastname: undefined,
        email: undefined
      }
      state.bookmarks = []
      //Clear cache for play progress
      state.episodeMap = {}
      state.seriesMap = {}
    },
    SET_USER(state, user: User) {
      state.user = user
    },
    SET_LIVE_CONFIG(state, liveConfig: LiveConfig) {
      state.liveConfig = liveConfig;
    },
    SET_PLATFORM_VERSION(state, version: string) {
      state.tvInfo.platformVersion = version;
    },
    SET_PLATFORM_MAJOR_VERSION(state, majorVersion: number) {
      if (!Number.isNaN(majorVersion)) {
        state.tvInfo.platformMajorVersion = majorVersion;
      }
    },
    SET_REGION(state, region) {
      state.region = region;
    },
    SET_API_LOCATION(state, locationResponse) {
      state.apiLocation = locationResponse;
    },
    SET_TV_SYSTEM(state, tvSystem) {      
      state.tvSystem = tvSystem;
    },
    SET_BOOKMARKS(state, bookmarks) {
      state.bookmarks = bookmarks
    },
    ADD_BOOKMARK(state, bookmark) {
      state.bookmarks = [bookmark].concat(state.bookmarks)
    },
    REMOVE_BOOKMARK(state, bookmarkId) {
      state.bookmarks = state.bookmarks.filter( (bookmark) => { return bookmark.id !== bookmarkId } )
    },
    CACHE_CONTENT(state, { videos = [], series = [] }: Partial<{ videos: VideoResponse[]; series: SeriesResponse[] }>) {
      videos.forEach((video) => {
        if (video.vid) {
          Vue.set(state.episodeMap, video.vid, video);
        }
      });
      series.forEach((entry) => {
        if (entry.slug) {
          Vue.set(state.seriesMap, entry.slug, entry);
        }
      });
    },
  },
  actions: {
    //Listen for API location changes
    OBSERVE_API_LOCATION({ commit, state }) {
      Api.CMS.onLocationChange((location) => {
        let locationChange = false;
        if (location.region !== state.region) {
          locationChange = true;
          console.log(`Location updated from API header: ${location.region}`);
          commit('SET_REGION', location.region);
        }

        if (!locationChange) {
          if (location.countryCode !== state.apiLocation.countryCode || location.city !== state.apiLocation.city) { 
            locationChange = true;
          }
        }

        if (locationChange) {
          commit('SET_API_LOCATION', location);
        }
      });
    },

    async GET_LIVE_CONFIG({ commit }) {
      const liveConfig = await Api.Tg4.getLiveConfig();
      commit('SET_LIVE_CONFIG', liveConfig);
    },
    INITIAL_SETUP_COMPLETE({ commit }) {
      commit('INITIAL_SETUP_COMPLETE', true);
    },
    SET_PLATFORM_VERSION({ commit }, version: string) {
      commit('SET_PLATFORM_VERSION', version);
    },
    SET_PLATFORM_MAJOR_VERSION({ commit }, majorVersion: number) {
      commit('SET_PLATFORM_MAJOR_VERSION', majorVersion);
    },
    SET_TV_SYSTEM({ commit }, tvSystem) {
      commit('SET_TV_SYSTEM', tvSystem);
    },
    CHANGE_LANGUAGE({ commit }, language: Language) {
      commit('CHANGE_LANGUAGE', language);
    },
    CHANGE_DEBUG_MODE({ commit }, debug: boolean) {
      commit('CHANGE_DEBUG_MODE', debug);
    },
    SET_USES_ONSCREEN_KEYBOARD({commit}, useOnScreenKeyboardForSearch: boolean){
      commit('SET_USES_ONSCREEN_KEYBOARD', useOnScreenKeyboardForSearch)
    },
    SET_TOKEN({commit}, token: string) {
      commit('SET_TOKEN', token)
    },
    LOGOUT({commit}) {
      commit('LOGOUT')
    },
    async GET_EPISODE({ commit, state }, params: { videoId: string, reload: boolean | undefined, adSupported: boolean | null, live: boolean | null, liveToken?: string  }) {

      let adConfigId: string | undefined = undefined
      if (params.adSupported == true) {
        adConfigId = state.liveConfig.smartTVLoggedInAdConfig
        if (!state.token) {
          adConfigId = state.liveConfig.smartTVLoggedOutAdConfig
        }
      }

      const liveToken = params.liveToken || undefined

      const video = await Api.Brightcove.getVideo(params.videoId, adConfigId, liveToken);

      return video;
      
    },
    async GET_EPISODE_MATERIAL_ID({ commit, state }, params: { materialId: string; adSupported: boolean | null, live: boolean | null }) {
      //Since we only have material Id we go straight to brightcove
      let adConfigId: string | undefined = undefined
      if (params.adSupported == true) {
        adConfigId = state.liveConfig.smartTVLoggedInAdConfig
        if (!state.token) {
          adConfigId = state.liveConfig.smartTVLoggedOutAdConfig
        }
      }

      //Live streams don't have material IDs so no need for live token
      const video = await Api.Brightcove.getVideoByReference(params.materialId, adConfigId);
      return video;
    },
    async GET_USER({ commit, state }) {
      if(state.user.id !== -1) {
        return state.user
      } else {
        const profile = await Api.PeachAPI.getUser()
        if (profile.user) {
          commit('SET_USER', profile?.user );
        }
        return profile.user;
      }
    },
    async GET_BOOKMARKS({ commit, state }, params: {reload: boolean}) {

      if (!state.bookmarks || state.bookmarks.length == 0 || params.reload) {
        const playlist = await Api.PeachAPI.getBookmarks()
        commit('SET_BOOKMARKS', playlist.bookmarks)
      }

    },
    async ADD_BOOKMARK({ commit }, params: {videoId: string}) {

      const bookmark = await Api.PeachAPI.addBookmark(params.videoId)
      
      commit('ADD_BOOKMARK', bookmark)

    },
    async REMOVE_BOOKMARK({ commit }, params: {bookmarkId: string}) {
      await Api.PeachAPI.removeBookmark(params.bookmarkId)
      commit('REMOVE_BOOKMARK', params.bookmarkId)
    },
  },
  getters: {
    debugModeEnabled(state) {
      return state.debugModeEnabled;
    },
    useOnScreenKeyboardForSearch(state) {
      return state.useOnScreenKeyboardForSearch
    },
    initialLaunch(state) {
      return state.initialLaunch;
    },
    currentLanguage(state) {
      return state.currentLanguage;
    },
    currentRegion(state) {
      return state.region;
    },
    apiLocation(state) {
      return state.apiLocation;
    },
    liveConfig(state) {
      return state.liveConfig;
    },
    platformVersion(state) {
      return state.tvInfo.platformVersion
    },
    platformMajorVersion(state) {
      return state.tvInfo.platformMajorVersion
    },
    getTVSystem(state) {
      return state.tvSystem
    },
    bookmark(state) {
      return (videoId: string) => { 
        return state.bookmarks.find((bookmark) => bookmark.itemId === videoId)
      };
    },
    isLoggedIn(state) {
      return state.token !== "";
    },
    cachedVideo(state) {
      return (vid: string) => state.episodeMap[vid];
    },
    cachedSeries(state) {
      return (slug: string) => state.seriesMap[slug];
    },
  },
  modules: {
  }
}

export default new Vuex.Store<State>(store);

